import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { IonIcon } from '@ionic/react';
import { eye, eyeOff } from 'ionicons/icons';
import { AuthContext } from '../context/AuthContext';
import { Form, Button, InputGroup, Alert } from 'react-bootstrap';
import AuthPage from '../components/layout/AuthPage';

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const token = searchParams.get('token');

  const passwordsMatch = password === confirmPassword && password.length > 0;
  const isValidPassword = password.length >= 8;

  const handleSetPassword = async (e) => {
    e.preventDefault();

    if (!token) {
      setError('Invalid access - no verification token provided');
      return;
    }

    if (!isValidPassword) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      const verifyResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || ''}/api/auth/verify-magic-link`,
        { token },
      );
      const { token: jwtToken, user } = verifyResponse.data;

      await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/user/set-password`,
        { password },
        { headers: { Authorization: `Bearer ${jwtToken}` } },
      );

      login(jwtToken, user);
      navigate('/app/dashboard', { replace: true });
    } catch (err) {
      console.error('Password setup failed:', err);
      setError(err.response?.data?.error || 'Failed to set password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthPage wide>
        <Alert variant="danger" className="mb-0 text-center">
          <h5 className="mb-2">Invalid Access</h5>
          <p>This link is invalid or has expired. Please try registering again.</p>
          <Button variant="secondary" onClick={() => navigate('/register')}>
            Go to Registration
          </Button>
        </Alert>
      </AuthPage>
    );
  }

  return (
    <AuthPage wide>
      <h1 className="auth-card__title">Set Your Password</h1>
      <p className="auth-card__subtitle">
        Welcome to Garden Sync! Please set a password for your account.
      </p>

      <Form onSubmit={handleSetPassword}>
        <Form.Group className="mb-3">
          <Form.Label>Password</Form.Label>
          <InputGroup>
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className={password && !isValidPassword ? 'is-invalid' : password && isValidPassword ? 'is-valid' : ''}
              required
            />
            <InputGroup.Text onClick={() => setShowPassword(!showPassword)} className="peek-toggle">
              <IonIcon icon={showPassword ? eyeOff : eye} />
            </InputGroup.Text>
          </InputGroup>
          {password && !isValidPassword && (
            <div className="invalid-feedback d-block">
              Password must be at least 8 characters long
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Confirm Password</Form.Label>
          <Form.Control
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            className={confirmPassword ? (passwordsMatch ? 'match' : 'mismatch') : ''}
            required
          />
        </Form.Group>

        <Button
          type="submit"
          className="button w-100"
          disabled={!passwordsMatch || !isValidPassword || isLoading}
        >
          {isLoading ? 'Setting Password...' : 'Set Password & Continue'}
        </Button>
      </Form>

      {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
    </AuthPage>
  );
};

export default SetPassword;